import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiRequest } from "../../../common/api/apiClient.js";
import type { CreateShareRequest, CreateWikiPageRequest, ShareDTO, UpdateWikiPageRequest, WikiPageDTO, WikiPageSummaryDTO } from "../../../common/api/types.js";

export const wikiPagesKey = ["wiki", "pages"] as const;
export const wikiPageKey = (pageId: string) => ["wiki", "page", pageId] as const;
export const wikiSharesKey = (pageId: string) => ["wiki", "shares", pageId] as const;

export function useWikiPages(query = "") {
  return useQuery({
    queryKey: [...wikiPagesKey, query],
    queryFn: () => apiRequest<WikiPageSummaryDTO[]>(`/wiki/pages${query ? `?query=${encodeURIComponent(query)}` : ""}`),
  });
}

export function useWikiPage(pageId: string | null) {
  return useQuery({
    queryKey: wikiPageKey(pageId ?? ""),
    queryFn: () => apiRequest<WikiPageDTO>(`/wiki/pages/${pageId as string}`),
    enabled: pageId !== null,
  });
}

export function useWikiShares(pageId: string, enabled: boolean) {
  return useQuery({ queryKey: wikiSharesKey(pageId), queryFn: () => apiRequest<ShareDTO[]>(`/wiki/pages/${pageId}/shares`), enabled });
}

export function useShareWikiPage(pageId: string) {
  const client = useQueryClient();
  return useMutation<ShareDTO, Error, CreateShareRequest>({
    mutationFn: (body) => apiRequest<ShareDTO>(`/wiki/pages/${pageId}/shares`, { method: "POST", body }),
    onSuccess: () => void client.invalidateQueries({ queryKey: wikiSharesKey(pageId) }),
  });
}

export function useRevokeWikiShare(pageId: string) {
  const client = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (shareId) => apiRequest<void>(`/wiki/pages/${pageId}/shares/${shareId}`, { method: "DELETE" }),
    onSuccess: () => void client.invalidateQueries({ queryKey: wikiSharesKey(pageId) }),
  });
}

export function useCreateWikiPage() {
  const client = useQueryClient();
  return useMutation<WikiPageDTO, Error, CreateWikiPageRequest>({
    mutationFn: (body) => apiRequest<WikiPageDTO>("/wiki/pages", { method: "POST", body }),
    onSuccess: (page) => {
      client.setQueryData(wikiPageKey(page.id), page);
      void client.invalidateQueries({ queryKey: wikiPagesKey });
    },
  });
}

export function useUpdateWikiPage(pageId: string) {
  const client = useQueryClient();
  return useMutation<WikiPageDTO, Error, UpdateWikiPageRequest>({
    mutationFn: (body) => apiRequest<WikiPageDTO>(`/wiki/pages/${pageId}`, { method: "PUT", body }),
    onSuccess: (page) => {
      client.setQueryData(wikiPageKey(page.id), page);
      void client.invalidateQueries({ queryKey: wikiPagesKey });
    },
  });
}

export function useDeleteWikiPage() {
  const client = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (pageId) => apiRequest<void>(`/wiki/pages/${pageId}`, { method: "DELETE" }),
    onSuccess: (_result, pageId) => {
      client.removeQueries({ queryKey: wikiPageKey(pageId) });
      void client.invalidateQueries({ queryKey: wikiPagesKey });
    },
  });
}
